import { useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { Button, Chip, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { usePreviewFile } from '@features/components/ClinicalData/hooks';
import { ConditionalRender } from '@ui';
import SectionRow from '@features/components/CaseOverview/components/SectionRow/SectionRow';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import {
  AberrantAnatomyType,
  ClinicalMediaFile,
  ClinicalMediaType,
  LiverQuality,
  NoLiverInjuryType,
  OrganType,
  ProcurementEnjuryType,
  SurgeonCaseSummaryData,
} from '@/api/apis/case';
import { formatTimestamp, formatValue, yesNo } from '@/utils/helpers';
import {
  flushTypeNames,
  liverOutcomeNames,
  liverQualityNames,
  aberrantAnatomyTypeNames,
  procurementEnjuryTypeNames,
  backtableInjuryTypeNames,
  noLiverInjuryTypeNames,
  assistanceTypeNames,
} from '@/constants/case/case.constants';

type SurgeonCaseSummaryModalProps = {
  surgeonCaseSummaryData: SurgeonCaseSummaryData;
  organType: OrganType;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

const getFilesAttachedString = (count: number) => `${count} file${count === 1 ? '' : 's'} attached`;

const formatListWithOther = <T extends string>(
  values: T[] | undefined,
  namesMap: Record<T, string>,
  otherValue: T,
  otherText?: string,
) =>
  values
    ?.map((value) => (value === otherValue ? otherText : namesMap[value]))
    .filter(Boolean)
    .join(', ');

export const SurgeonCaseSummaryModal = ({
  surgeonCaseSummaryData,
  organType,
  onOpen,
  isOpen,
  onClose,
}: SurgeonCaseSummaryModalProps) => {
  const [mediaList, setMediaList] = useState<ClinicalMediaType[]>([]);
  const { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose } = usePreviewFile();

  const getPreviewFiles = (files: ClinicalMediaFile[]) => {
    const convertedFiles = files.map((file) => ({ file }));
    setMediaList(convertedFiles);
    handlePreviewOpen(convertedFiles[0].file.fileId);
  };

  const handleMediaPreviewClose = () => {
    setMediaList([]);
    handlePreviewClose();
  };

  const {
    isSurgicalInjuryOccurred,
    isOrganNotUsed,
    vessels,
    procurementInjury,
    kidneyProcurement,
    assistance,
    summary,
    times,
    generalCaseDetails,
  } = surgeonCaseSummaryData;

  const {
    liverQuality,
    estimatedSteatosis,
    flush,
    liverOutcome,
    isDonorExpiredInTime,
    warmIschemiaTime,
    warmIschemiaTimeTimeZone,
  } = generalCaseDetails ?? {};

  const aberrantAnatomyText = formatListWithOther(
    vessels?.aberrantAnatomyTypeData?.aberrantAnatomyTypes,
    aberrantAnatomyTypeNames,
    AberrantAnatomyType.Other,
    vessels?.aberrantAnatomyTypeData?.text,
  );
  
  const noLiverInjuryText = formatListWithOther(
    procurementInjury?.noLiverInjuryTypeData?.noLiverInjuries,
    noLiverInjuryTypeNames,
    NoLiverInjuryType.Other,
    procurementInjury?.noLiverInjuryTypeData?.text,
  );

  return (
    <Modal
      className="scroll"
      scrollBehavior="outside"
      isOpen={isOpen}
      onOpenChange={onOpen}
      onClose={onClose}
      size="3xl"
      closeButton={<IoClose size={42} className="z-1000" />}
      classNames={{
        closeButton: 'text-base-10 mt-6 mr-4',
        header: 'text-hl! flex flex-col gap-4 text-base-20 font-secondary pt-6 pb-4',
        body: 'flex flex-col mx-6 p-0 pb-6 gap-6',
      }}
    >
      <ModalContent>
        <ModalHeader>
          <div>Surgeon Case Summary</div>
          <Divider />
        </ModalHeader>
        <ModalBody>
          {organType === OrganType.Heart && (
            <>
              <SectionRow
                label="Was there any surgical injury/challenge requiring additional surgical steps?"
                fontNormal
                truncate={false}
                centerItems={false}
              >
                <Chip size="sm">{yesNo(isSurgicalInjuryOccurred)}</Chip>
              </SectionRow>
              <SectionRow label="Did it result in non-use of the organ?" fontNormal>
                <Chip size="sm">{yesNo(isOrganNotUsed)}</Chip>
              </SectionRow>
              <SectionRow label="Please describe the injury/challenge" truncate={false} centerItems={false} fontNormal>
                {surgeonCaseSummaryData.challengeDescription}
              </SectionRow>
            </>
          )}
          {generalCaseDetails && organType === OrganType.Liver && (
            <>
              {/* GENERAL CASE DETAILS */}
              <div className="text-l-sb">General Case Details</div>
              <SectionRow label="Did the donor expire in time?" fontNormal>
                <Chip size="sm">{yesNo(isDonorExpiredInTime)}</Chip>
              </SectionRow>
              <SectionRow label="Warm ischemia time" fontNormal>
                <ConditionalRender show={!!warmIschemiaTime && !!warmIschemiaTimeTimeZone}>
                  <Chip size="sm">{formatValue(formatTimestamp(warmIschemiaTime, warmIschemiaTimeTimeZone))}</Chip>
                </ConditionalRender>
              </SectionRow>
              <SectionRow label="Liver Quality" fontNormal>
                {liverQuality?.liverQualities.map((quality) => (
                  <Chip size="sm" className="mr-2">
                    {quality === LiverQuality.Other
                      ? formatValue(liverQuality.text)
                      : formatValue(liverQualityNames[quality])}
                  </Chip>
                ))}
              </SectionRow>
              <SectionRow label="Estimated Steatosis (%)" fontNormal>
                <ConditionalRender show={!!estimatedSteatosis}>
                  <Chip size="sm">{formatValue(estimatedSteatosis)}</Chip>
                </ConditionalRender>
              </SectionRow>
              <SectionRow label="Flush" fontNormal>
                <ConditionalRender show={!!flush}>
                  <Chip size="sm">{formatValue(flushTypeNames[flush!])}</Chip>
                </ConditionalRender>
              </SectionRow>
              <SectionRow label="Liver Outcome" fontNormal truncate={false}>
                {formatValue(liverOutcomeNames[liverOutcome!])}
              </SectionRow>
              {/* TIMES */}
              {!!times && (
                <>
                  <div className="text-l-sb">Times</div>
                  <SectionRow label="Time of skin incision" fontNormal>
                    {formatTimestamp(times.skinIncision, times.skinIncisionTimeZone)}
                  </SectionRow>
                  <SectionRow label="Time of cross-clamp and flush" fontNormal>
                    {formatTimestamp(times.crossClampAndFlush, times.crossClampAndFlushTimeZone)}
                  </SectionRow>
                  <SectionRow label="Time of hepatectomy completion" fontNormal>
                    {formatTimestamp(times.hepatectomyCompletion, times.hepatectomyCompletionTimeZone)}
                  </SectionRow>
                  <SectionRow
                    label="Time of completion of back table dissection and liver cannulation"
                    centerItems={false}
                    fontNormal
                  >
                    {formatTimestamp(
                      times.backTableDissectionAndLiverCannulationCompletion,
                      times.backTableDissectionAndLiverCannulationCompletionTimeZone,
                    )}
                  </SectionRow>
                </>
              )}
              {/* VESSELS */}
              {!!vessels && (
                <>
                  <div className="text-l-sb">Vessels</div>
                  <SectionRow label="Aberrant Arterial Anatomy" fontNormal>
                    <Chip size="sm">{yesNo(vessels.aberrantArterialAnatomy)}</Chip>
                  </SectionRow>
                  <SectionRow label="Type of Aberrant Anatomy" fontNormal truncate={false} centerItems={false}>
                    <div className="whitespace-normal wrap-break-word">{aberrantAnatomyText}</div>
                  </SectionRow>
                  <SectionRow label="Reconstruction description" fontNormal centerItems={false} truncate={false}>
                    {vessels.reconstructionDescription}
                  </SectionRow>
                  <SectionRow label="Reconstruction Details" fontNormal>
                    <Button
                      size="sm"
                      variant="light"
                      disableRipple
                      className="text-m-m! underline"
                      onClick={() => getPreviewFiles(vessels.mediaFiles)}
                    >
                      {getFilesAttachedString(vessels.mediaFiles.length)}
                    </Button>
                  </SectionRow>
                </>
              )}
              {/* PROCUREMENT INJURY */}
              {!!procurementInjury && (
                <>
                  <div className="text-l-sb">Procurement Injury</div>
                  <SectionRow label="Procurement injury" fontNormal truncate={false} centerItems={false}>
                    <div className="flex flex-wrap gap-2">
                      {procurementInjury.procurementInjuryTypeData?.procurementInjuries?.map((procurementInjuries) => (
                        <Chip size="sm" key={procurementInjuries}>
                          {formatValue(
                            procurementInjuries === ProcurementEnjuryType.Other
                              ? procurementInjury.procurementInjuryTypeData.text
                              : procurementEnjuryTypeNames[procurementInjuries],
                          )}
                        </Chip>
                      ))}
                    </div>
                  </SectionRow>
                  <SectionRow label="Backtable injury" fontNormal truncate={false} centerItems={false}>
                    <div className="flex flex-wrap gap-2">
                      {procurementInjury.backtableInjuries?.map((backtableInjurie) => (
                        <Chip size="sm" key={backtableInjurie}>
                          {formatValue(backtableInjuryTypeNames[backtableInjurie])}
                        </Chip>
                      ))}
                    </div>
                  </SectionRow>
                  <SectionRow label="Non-Liver Injury (TMDX Surgeon)" fontNormal truncate={false} centerItems={false}>
                    <div className="whitespace-normal wrap-break-word">{noLiverInjuryText}</div>
                  </SectionRow>
                  <SectionRow label="Injury Details" truncate={false} centerItems={false} fontNormal>
                    {formatValue(procurementInjury.details)}
                  </SectionRow>
                  <SectionRow label="Uploaded Files" fontNormal>
                    <Button
                      size="sm"
                      variant="light"
                      disableRipple
                      className="text-m-m! underline"
                      onClick={() => getPreviewFiles(procurementInjury.mediaFiles)}
                    >
                      {getFilesAttachedString(procurementInjury.mediaFiles.length)}
                    </Button>
                  </SectionRow>
                </>
              )}
              {/* KIDNEY PROCUREMENT */}
              {!!kidneyProcurement && (
                <>
                  <div className="text-l-sb">Kidney procurement</div>
                  <SectionRow label="Kidneys Procurement" fontNormal>
                    <Chip size="sm">{yesNo(kidneyProcurement.isKidneysProcuredForTransplantAfterOpoRequest)}</Chip>
                  </SectionRow>
                  <SectionRow label="OPO Name" fontNormal>
                    {kidneyProcurement.opoName}
                  </SectionRow>
                  <SectionRow label="Signed donor op note" fontNormal>
                    <Button
                      size="sm"
                      variant="light"
                      disableRipple
                      className="text-m-m! underline"
                      onClick={() => getPreviewFiles(kidneyProcurement.mediaFiles)}
                    >
                      {getFilesAttachedString(kidneyProcurement.mediaFiles.length)}
                    </Button>
                  </SectionRow>
                </>
              )}
              {/* ASSISTANCE */}
              {!!assistance && (
                <>
                  <div className="text-l-sb">Assistance</div>
                  <SectionRow label="Assistance in the donor" fontNormal truncate={false}>
                    {assistance.assistanceInTheDonor?.map((assistanceInTheDonor, index) =>
                      index !== 0
                        ? ` ,${assistanceTypeNames[assistanceInTheDonor]}`
                        : `${assistanceTypeNames[assistanceInTheDonor]}`,
                    )}
                  </SectionRow>
                  <SectionRow label="Assistance during backtable preparation of Liver" fontNormal truncate={false}>
                    {assistance.backtablePreparationAssistance?.map((backtablePreparationAssistance, index) =>
                      index !== 0
                        ? ` ,${assistanceTypeNames[backtablePreparationAssistance]}`
                        : `${assistanceTypeNames[backtablePreparationAssistance]}`,
                    )}
                  </SectionRow>
                </>
              )}
              {/* SUMMARY */}
              {!!summary && (
                <>
                  <div className="text-l-sb">Summary</div>
                  <SectionRow label="Uploaded Documents" fontNormal>
                    <Button
                      size="sm"
                      variant="light"
                      disableRipple
                      className="text-m-m! underline"
                      onClick={() => getPreviewFiles(summary.mediaFiles)}
                    >
                      {getFilesAttachedString(summary.mediaFiles.length)}
                    </Button>
                  </SectionRow>
                  <SectionRow label="Details (description of injury)" truncate={false} centerItems={false} fontNormal>
                    {summary.text}
                  </SectionRow>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter />
      </ModalContent>
      {isPreviewOpen && (
        <MediaPreview
          media={mediaList || []}
          onClose={handleMediaPreviewClose}
          open={isPreviewOpen}
          selectedMediaId={previewFileId}
        />
      )}
    </Modal>
  );
};
